import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search as SearchIcon } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ProductCard } from "@/components/site/ProductCard";
import { Seo } from "@/components/seo/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Search = ({ cartCount, onAdd }) => {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${API}/search?q=${encodeURIComponent(q)}`).then((r) => {
      setResults(r.data || []);
      setLoading(false);
    });
    window.scrollTo({ top: 0 });
  }, [q]);

  return (
    <>
      <Seo
        title={q ? `Search results for "${q}"` : "Search products"}
        description={q ? `${results.length} results matching "${q}" in the Discount Fillers catalog.` : "Search our wholesale catalog of authentic aesthetic medical supplies."}
        path={`/search${q ? `?q=${encodeURIComponent(q)}` : ""}`}
        noindex
      />
      <Header cartCount={cartCount} />
      <div className="max-w-[1400px] mx-auto px-6 pt-28 md:pt-32 pb-20" data-testid="search-page">
        <div className="mb-10">
          <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">
            Search results
          </div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight leading-tight">
            <span className="font-thin">Results for </span>
            <span className="font-black">&ldquo;{q}&rdquo;</span>
          </h1>
          {!loading && (
            <p className="mt-3 text-sm text-neutral-600">{results.length} product{results.length === 1 ? "" : "s"} found</p>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-neutral-200">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-neutral-50 animate-pulse" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-neutral-300" data-testid="search-empty">
            <SearchIcon className="w-8 h-8 mx-auto text-neutral-400" />
            <h3 className="mt-4 font-heading text-xl font-bold">No results</h3>
            <p className="text-sm text-neutral-500 mt-2">Try a different brand or category name.</p>
            <Link to="/" className="mt-6 inline-block border-b border-black pb-1 text-sm font-medium">← Back to catalog</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-l border-t border-neutral-200" data-testid="search-grid">
            {results.map((p) => (
              <ProductCard key={p.id} product={p} onAdd={onAdd} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};
